import React from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartTheme } from '@modules/ui-components/hooks/useChartTheme';
import type {
  CreativeCalendarDynamics as CreativeCalendarDynamicsContract,
  CreativeCalendarDynamicsCell,
  CreativeCalendarDynamicsProduct,
} from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { DataThroughCaption } from '../DataThroughCaption';
import { formatMinorMoney } from './creativeDecisionFormat';

interface DayPoint {
  date: string;
  initialPaymentCount: number;
  renewalCount: number;
  trialConversionCount: number;
  trialStartCount: number;
  /** Major units; undefined = blocked/absent gross → chart gap, never zero. */
  gross?: number;
  grossBlocked: boolean;
  unreconciled: boolean;
}

/**
 * Day-grain money/conversion dynamics per paywall product. Gross is AUDIT
 * context only — the decision basis (contribution) stays in MoneySection.
 * Currencies are never summed: >1 original currency renders one chart per
 * currency group.
 */
export function CreativeCalendarDynamics({
  dynamics,
  productLabels,
}: {
  dynamics?: CreativeCalendarDynamicsContract;
  /** Optional screen-config id→name overrides (extractPaywallProductLabels). */
  productLabels?: Record<string, string>;
}): React.ReactElement | null {
  const chart = useChartTheme();
  const [excludedProducts, setExcludedProducts] = React.useState<ReadonlySet<string>>(new Set());
  if (!dynamics) return null;

  const stateColor = dynamics.state === 'available'
    ? 'success'
    : dynamics.state === 'partial' ? 'warning' : 'default';
  const products = dynamics.products;
  const selectedKeys = new Set(
    products.map(productKey).filter((key) => !excludedProducts.has(key)),
  );
  const filteredCells = dynamics.cells.filter((cell) => selectedKeys.has(cellProductKey(cell)));
  const currencyGroups = groupByCurrency(filteredCells);

  return (
    <Box
      component="section"
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerCalendarDynamics}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>Calendar dynamics</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={stateColor}
          label={dynamics.state}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerCalendarDynamicsState}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Daily conversions by paywall product · gross shown as audit context only
      </Typography>

      {dynamics.state === 'unavailable' ? (
        <Alert severity="info">
          {`No calendar money cells exist for this deployment in the selected window (${humanReasons(dynamics.reasonCodes)}). Nothing is estimated.`}
        </Alert>
      ) : (
        <>
          {products.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mb: 1.5 }}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerCalendarDynamicsProductFilter}
            >
              {products.map((product) => {
                const key = productKey(product);
                const selected = !excludedProducts.has(key);
                return (
                  <Box
                    key={key}
                    component="button"
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setExcludedProducts((previous) => {
                      const next = new Set(previous);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      return next;
                    })}
                    data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerCalendarDynamicsProduct(key)}
                    sx={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      font: 'inherit',
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      border: 1,
                      borderColor: selected ? 'primary.main' : 'divider',
                      boxShadow: selected ? (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
                      bgcolor: 'transparent',
                      color: 'text.primary',
                      opacity: selected ? 1 : 0.6,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                      {productDisplayName(product, productLabels)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {productTypeLabel(product)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', fontStyle: product.priceState === 'provider' ? 'normal' : 'italic' }}
                      color={product.priceState === 'provider' ? 'text.primary' : 'text.secondary'}
                    >
                      {productNominalLabel(product)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}

          {dynamics.state === 'partial' && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {`Some days are partial: ${humanReasons(dynamics.reasonCodes)}. Blocked days render as gaps — counts and money are never invented.`}
            </Alert>
          )}

          {currencyGroups.length === 0 ? (
            <Alert severity="info">No day cells match the selected products.</Alert>
          ) : currencyGroups.map((group) => {
            const points = buildDayPoints(group.cells);
            const blockedDays = points.filter((point) => point.grossBlocked).length;
            const accessibleSummary = [
              `Daily paywall conversions and audit gross in ${group.currency}.`,
              `${points.length} days.`,
              blockedDays > 0 ? `${blockedDays} blocked day${blockedDays === 1 ? '' : 's'} shown as gaps.` : '',
            ].filter(Boolean).join(' ');
            return (
              <Box
                key={group.currency}
                sx={{ mb: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{group.currency}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    bars: conversions · line: gross (audit)
                  </Typography>
                </Stack>
                <Box role="img" aria-label={accessibleSummary} sx={{ width: '100%', height: { xs: 220, sm: 260 } }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                      <XAxis dataKey="date" tick={chart.axisTick} tickFormatter={shortDate} />
                      <YAxis yAxisId="counts" tick={chart.axisTick} allowDecimals={false} />
                      <YAxis
                        yAxisId="gross"
                        orientation="right"
                        tick={chart.axisTick}
                        tickFormatter={(value: number) => compactMoney(value, group.currency)}
                      />
                      <RechartsTooltip
                        cursor={{ fill: chart.cursorFill }}
                        formatter={(value, name) => [
                          typeof value === 'number'
                            ? (name === 'Gross (audit)'
                              ? formatMinorMoney(Math.round(value * 10 ** group.currencyExponent), group.currency, group.currencyExponent)
                              : `${value}`)
                            : 'n/a',
                          String(name),
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="counts" stackId="conversions" dataKey="initialPaymentCount" name="Initial payments" fill={chart.primary} />
                      <Bar yAxisId="counts" stackId="conversions" dataKey="renewalCount" name="Renewals" fill={chart.info} />
                      <Bar yAxisId="counts" stackId="conversions" dataKey="trialConversionCount" name="Trial conversions" fill={chart.positive} />
                      <Line
                        yAxisId="gross"
                        type="monotone"
                        dataKey="gross"
                        name="Gross (audit)"
                        stroke={chart.reference}
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        connectNulls={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
                <Stack component="ol" role="list" spacing={0.5} sx={{ m: 0, mt: 1, p: 0, listStyle: 'none' }}>
                  {points.map((point) => (
                    <Box
                      component="li"
                      key={`${group.currency}:${point.date}`}
                      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerCalendarDynamicsDay(point.date)}
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', py: 0.25, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 78 }}>{point.date}</Typography>
                      <Typography variant="caption">
                        {`initial ${formatCount(point.initialPaymentCount)} · renewals ${formatCount(point.renewalCount)} · trial conv ${formatCount(point.trialConversionCount)} · trial starts ${formatCount(point.trialStartCount)}`}
                      </Typography>
                      <Typography variant="caption" color={point.grossBlocked ? 'warning.main' : 'text.secondary'}>
                        {point.grossBlocked
                          ? 'gross blocked (gap)'
                          : `gross ${point.gross === undefined
                            ? 'n/a'
                            : formatMinorMoney(Math.round(point.gross * 10 ** group.currencyExponent), group.currency, group.currencyExponent)} (audit)`}
                        {point.unreconciled ? ' · unreconciled' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            );
          })}

          <DataThroughCaption
            dataThrough={dynamics.dataThrough}
            testId={`${CREATIVE_DECISIONS_TEST_IDS.drawerCalendarDynamics}-data-through`}
          />
        </>
      )}
    </Box>
  );
}

export function productKey(product: Pick<CreativeCalendarDynamicsProduct, 'productId' | 'priceId'>): string {
  return `${product.productId ?? ''}|${product.priceId ?? ''}`;
}

function cellProductKey(cell: CreativeCalendarDynamicsCell): string {
  return `${cell.productId ?? ''}|${cell.priceId ?? ''}`;
}

function productDisplayName(
  product: CreativeCalendarDynamicsProduct,
  productLabels?: Record<string, string>,
): string {
  const fromLabels = product.productId ? productLabels?.[product.productId] : undefined;
  return fromLabels
    ?? product.productName
    ?? product.priceNickname
    ?? shortId(product.productId ?? product.priceId ?? 'unknown product');
}

function productTypeLabel(product: CreativeCalendarDynamicsProduct): string {
  const interval = product.interval ?? intervalFromBilling(product.billingInterval);
  const cadence = interval
    ? (product.intervalCount && product.intervalCount > 1
      ? `every ${product.intervalCount} ${interval}s`
      : `${interval}ly`.replace('dayly', 'daily'))
    : undefined;
  const mode = product.purchaseMode === 'one_time'
    ? 'one-time purchase'
    : cadence ? `${cadence} subscription` : 'subscription';
  const entry = product.subscriptionEntry && product.subscriptionEntry !== 'no_trial'
    ? ` · ${product.subscriptionEntry.replaceAll('_', ' ')}`
    : '';
  return `${mode}${entry}`;
}

function productNominalLabel(product: CreativeCalendarDynamicsProduct): string {
  if (
    product.priceState !== 'provider'
    || product.unitAmountMinor === undefined
    || !product.currency
  ) {
    return 'price unavailable';
  }
  const exponent = product.currencyExponent ?? 2;
  const amount = formatMinorMoney(product.unitAmountMinor, product.currency, exponent);
  const interval = product.interval;
  const per = interval
    ? ` / ${product.intervalCount && product.intervalCount > 1 ? `${product.intervalCount} ${interval}s` : interval}`
    : '';
  return `${amount}${per}`;
}

function intervalFromBilling(billingInterval?: string): CreativeCalendarDynamicsProduct['interval'] {
  switch (billingInterval) {
    case 'day': case 'week': case 'month': case 'year':
      return billingInterval;
    default:
      return undefined;
  }
}

function groupByCurrency(cells: CreativeCalendarDynamicsCell[]): Array<{
  currency: string;
  currencyExponent: number;
  cells: CreativeCalendarDynamicsCell[];
}> {
  const groups = new Map<string, { currency: string; currencyExponent: number; cells: CreativeCalendarDynamicsCell[] }>();
  for (const cell of cells) {
    const existing = groups.get(cell.originalCurrency);
    if (existing) existing.cells.push(cell);
    else groups.set(cell.originalCurrency, {
      currency: cell.originalCurrency,
      currencyExponent: cell.currencyExponent,
      cells: [cell],
    });
  }
  return [...groups.values()].sort((left, right) => left.currency.localeCompare(right.currency));
}

function buildDayPoints(cells: CreativeCalendarDynamicsCell[]): DayPoint[] {
  const byDate = new Map<string, CreativeCalendarDynamicsCell[]>();
  for (const cell of cells) {
    const existing = byDate.get(cell.calendarDate);
    if (existing) existing.push(cell);
    else byDate.set(cell.calendarDate, [cell]);
  }
  return [...byDate.entries()]
    .map(([date, dayCells]) => {
      const grossBlocked = dayCells.some((cell) => cell.grossBasisStatus === 'blocked'
        || cell.grossCollectedMinor === null);
      const exponent = dayCells[0].currencyExponent;
      const grossMinor = grossBlocked
        ? undefined
        : dayCells.reduce((sum, cell) => sum + (cell.grossCollectedMinor ?? 0), 0);
      return {
        date,
        initialPaymentCount: sumCount(dayCells, 'initialPaymentCount'),
        renewalCount: sumCount(dayCells, 'renewalCount'),
        trialConversionCount: sumCount(dayCells, 'trialConversionCount'),
        trialStartCount: sumCount(dayCells, 'trialStartCount'),
        ...(grossMinor === undefined ? {} : { gross: grossMinor / 10 ** exponent }),
        grossBlocked,
        unreconciled: dayCells.some((cell) => cell.reconciliationStatus === 'unreconciled'),
      };
    })
    .sort((left, right) => left.date.localeCompare(right.date));
}

function sumCount(
  cells: CreativeCalendarDynamicsCell[],
  key: 'initialPaymentCount' | 'renewalCount' | 'trialConversionCount' | 'trialStartCount',
): number {
  return roundCount(cells.reduce((sum, cell) => sum + (cell[key] ?? 0), 0));
}

function roundCount(value: number): number {
  return Math.round(value * 100) / 100;
}

function formatCount(value: number): string {
  return Number.isInteger(value) ? `${value}` : value.toFixed(2);
}

function shortId(value: string): string {
  return value.length <= 14 ? value : `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function shortDate(value: string): string {
  return typeof value === 'string' && value.length >= 10 ? value.slice(5) : String(value);
}

function compactMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(0)}`;
  }
}

function humanReasons(reasonCodes: readonly string[]): string {
  return reasonCodes.length
    ? reasonCodes.map((reason) => reason.replaceAll('_', ' ')).join(' · ')
    : 'no registered reason';
}
